import * as React from 'react';
import {
    CheckboxVisibility,
    DetailsList,
    Fabric,
    IObjectWithKey,
    ISelection,
    PrimaryButton,
    ScrollablePane,
    Selection,
    SelectionMode,
    Sticky,
    StickyPositionType,
} from '@fluentui/react';
import { getTheme, ITheme, mergeStyleSets } from '@fluentui/react/lib/Styling';
import { assignKundeBetreuer, assignKundeTelBetreuer, Kunde, KundeFilter, useKundeList } from 'api/kunde';
import { LoadingIndicator } from 'layout/LoadingIndicator/LoadingIndicator';
import { betreuerByNrSelector } from 'store/lookups/selector';
import { useSelector } from 'react-redux';
import { createKundeUrl, formatMoney } from 'utils/formatter';
import { OpenLink } from 'formComponents/OpenLink/OpenLink';
import { ConfirmMessage } from 'formComponents/ConfirmMessage/ConfirmMessage';
import { BetreuerById } from 'formComponents/lookups/BetreuerById';
import moment from 'moment-timezone';

const theme: ITheme = getTheme();

const styles = mergeStyleSets({
    container: {
        position: 'relative',
        display: 'grid',
        gridTemplateRows: 'max-content max-content 1fr',
        gridRowGap: 5,
    },
    mailListHeader: {
        fontSize: 16,
        marginLeft: 6,
        marginTop: 6,
        marginBottom: 6,
    },
    copyMailList: {
        fontSize: 10,
        marginLeft: 15,
    },
    actionButtons: {
        display: 'grid',
        gridTemplateColumns: '1fr max-content max-content',
        gridColumnGap: 5,
    },
    summary: {
        display: 'grid',
        gridTemplateColumns: '1fr repeat(5, 150px)',
        gridColumnGap: 5,
    },
    summaryCell: {
        textAlign: 'right',
    },
    list: {
        position: 'relative',
        // overflow: 'scroll',
    },
    defaultCell: {
        fontSize: theme.fonts.medium.fontSize,
    },
    moneyCell: {
        fontSize: theme.fonts.medium.fontSize,
        textAlign: 'right',
    },
    row: {
        backgroundColor: 'lightblue !important',
    },
    select: {
        cursor: 'pointer',
    },
    confirmContainer: {
        minHeight: 300,
    },
});

interface BetreuerzuweisungProps {
    kundeFilter: KundeFilter | null;
}

export const Betreuerzuweisung: React.FC<BetreuerzuweisungProps> = ({ kundeFilter }) => {
    const kundeList = useKundeList(kundeFilter || {}, { enabled: !!kundeFilter });
    const betreuerByNr = useSelector(betreuerByNrSelector);

    const [selectedKundeList, setSelectedKundeList] = React.useState<Kunde[]>([]);

    const selection = React.useRef<ISelection<IObjectWithKey>>(
        new Selection({
            selectionMode: SelectionMode.multiple,
            getKey: (item) => String((item as Kunde).nr),
            onSelectionChanged: () => {
                setSelectedKundeList(selection.current.getSelection() as Kunde[]);
            },
        }),
    );

    const [assignBetreuerField, setAssignBetreuerField] = React.useState<'betreuer' | 'telBetreuer' | null>(null);
    const [assignBetreuerNr, setAssignBetreuerNr] = React.useState<number | null>(null);
    const [assigning, setAssigning] = React.useState<number | null>(null);

    const {
        umsatzLfd,
        selectedUmsatzLfd,
        umsatzVj1,
        selectedUmsatzVj1,
        umsatzVj2,
        selectedUmsatzVj2,
        umsatzVj3,
        selectedUmsatzVj3,
    } = React.useMemo(() => {
        let newUmsatzLfd = 0;
        let newUmsatzVj1 = 0;
        let newUmsatzVj2 = 0;
        let newUmsatzVj3 = 0;

        (kundeList.data || []).forEach((kunde) => {
            newUmsatzLfd += kunde.umsatzLfd;
            newUmsatzVj1 += kunde.umsatzVj1;
            newUmsatzVj2 += kunde.umsatzVj2;
            newUmsatzVj3 += kunde.umsatzVj3;
        });

        let newSelectedUmsatzLfd = 0;
        let newSelectedUmsatzVj1 = 0;
        let newSelectedUmsatzVj2 = 0;
        let newSelectedUmsatzVj3 = 0;

        selectedKundeList.forEach((selectedKunde) => {
            newSelectedUmsatzLfd += selectedKunde.umsatzLfd;
            newSelectedUmsatzVj1 += selectedKunde.umsatzVj1;
            newSelectedUmsatzVj2 += selectedKunde.umsatzVj2;
            newSelectedUmsatzVj3 += selectedKunde.umsatzVj3;
        });

        return {
            umsatzLfd: newUmsatzLfd,
            selectedUmsatzLfd: newSelectedUmsatzLfd,
            umsatzVj1: newUmsatzVj1,
            selectedUmsatzVj1: newSelectedUmsatzVj1,
            umsatzVj2: newUmsatzVj2,
            selectedUmsatzVj2: newSelectedUmsatzVj2,
            umsatzVj3: newUmsatzVj3,
            selectedUmsatzVj3: newSelectedUmsatzVj3,
        };
    }, [kundeList, selectedKundeList]);

    const onRenderDetailsHeader = React.useCallback(
        (detailsHeaderProps, defaultRender) => (
            <Sticky stickyPosition={StickyPositionType.Header}>
                {defaultRender
                    ? defaultRender({
                          ...detailsHeaderProps,
                      } as never)
                    : null}
            </Sticky>
        ),
        [],
    );

    const renderBetreuer = (column: 'betreuer' | 'telBetreuer', label: string) => (
        <BetreuerById
            value={assignBetreuerNr}
            label={label}
            disabled={assigning !== null}
            onChange={(newValue): void => {
                setAssignBetreuerNr(newValue);
            }}
        />
    );

    const assignBetreuerLabel =
        assignBetreuerField === 'betreuer'
            ? 'Betreuer'
            : assignBetreuerField === 'telBetreuer'
            ? 'Tel.-Betreuer'
            : null;

    const thisYear = parseInt(moment().format('YYYY'), 10);

    return (
        <Fabric className={styles.container}>
            {kundeList.isLoading || kundeList.isFetching ? (
                <LoadingIndicator />
            ) : (
                <>
                    <div className={styles.actionButtons}>
                        <div />
                        <PrimaryButton
                            disabled={selectedKundeList.length === 0}
                            text="Betreuer zuweisen"
                            onClick={() => setAssignBetreuerField('betreuer')}
                        />
                        <PrimaryButton
                            disabled={selectedKundeList.length === 0}
                            text="Tel.-Betreuer zuweisen"
                            onClick={() => setAssignBetreuerField('telBetreuer')}
                        />
                    </div>
                    <div className={styles.summary}>
                        <div />
                        <div className={styles.summaryCell}>
                            Ausgewählt
                            <br />
                            <strong>{selectedKundeList.length}</strong>
                            <br />
                            von <strong>{(kundeList.data || []).length}</strong>
                        </div>
                        <div className={styles.summaryCell}>
                            Umsatz {thisYear}
                            <br />
                            <strong>{formatMoney(selectedUmsatzLfd)}</strong>
                            <br />
                            von <strong>{formatMoney(umsatzLfd)}</strong>
                        </div>
                        <div className={styles.summaryCell}>
                            Umsatz {thisYear - 1}
                            <br />
                            <strong>{formatMoney(selectedUmsatzVj1)}</strong>
                            <br />
                            von <strong>{formatMoney(umsatzVj1)}</strong>
                        </div>
                        <div className={styles.summaryCell}>
                            Umsatz {thisYear - 2}
                            <br />
                            <strong>{formatMoney(selectedUmsatzVj2)}</strong>
                            <br />
                            von <strong>{formatMoney(umsatzVj2)}</strong>
                        </div>
                        <div className={styles.summaryCell}>
                            {thisYear - 3}
                            <br />
                            <strong>{formatMoney(selectedUmsatzVj3)}</strong>
                            <br />
                            von <strong>{formatMoney(umsatzVj3)}</strong>
                        </div>
                    </div>
                    <div className={styles.list}>
                        <ScrollablePane>
                            <DetailsList
                                items={kundeList.data || []}
                                compact
                                selectionMode={SelectionMode.multiple}
                                selectionPreservedOnEmptyClick
                                checkboxVisibility={CheckboxVisibility.always}
                                onRenderDetailsHeader={onRenderDetailsHeader}
                                selection={selection.current}
                                columns={[
                                    {
                                        key: 'name1',
                                        name: 'Name',
                                        fieldName: 'name1',
                                        minWidth: 200,
                                        maxWidth: 1000,
                                        isResizable: true,
                                        className: styles.defaultCell,
                                        onRender: (item: Kunde) => (
                                            <OpenLink link={createKundeUrl(item.nr.toString())} label={item.name1} />
                                        ),
                                    },
                                    {
                                        key: 'plz',
                                        name: 'PLZ',
                                        fieldName: 'plz',
                                        minWidth: 50,
                                        maxWidth: 70,
                                        isResizable: true,
                                        className: styles.defaultCell,
                                    },
                                    {
                                        key: 'ort',
                                        name: 'Ort',
                                        fieldName: 'ort',
                                        minWidth: 100,
                                        maxWidth: 300,
                                        isResizable: true,
                                        className: styles.defaultCell,
                                    },
                                    {
                                        key: 'betreuer',
                                        name: 'Betreuer',
                                        fieldName: 'betreuer',
                                        minWidth: 150,
                                        maxWidth: 300,
                                        isResizable: true,
                                        className: styles.defaultCell,
                                        onRender: (item: Kunde) =>
                                            item.betreuer ? betreuerByNr[item.betreuer]?.name || item.betreuer : '',
                                    },
                                    {
                                        key: 'telBetreuer',
                                        name: 'Tel.-Betreuer',
                                        fieldName: 'telBetreuer',
                                        minWidth: 150,
                                        maxWidth: 300,
                                        isResizable: true,
                                        className: styles.defaultCell,
                                        onRender: (item: Kunde) =>
                                            item.telBetreuer
                                                ? betreuerByNr[item.telBetreuer]?.name || item.telBetreuer
                                                : '',
                                    },
                                    {
                                        key: 'umsatzLfd',
                                        name: `Umsatz ${thisYear}`,
                                        fieldName: 'umsatzLfd',
                                        minWidth: 100,
                                        maxWidth: 100,
                                        isResizable: false,
                                        className: styles.moneyCell,
                                        onRender: (item: Kunde) => formatMoney(item?.umsatzLfd),
                                    },
                                    {
                                        key: 'umsatzVj1',
                                        name: `Umsatz ${thisYear - 1}`,
                                        fieldName: 'umsatzVj1',
                                        minWidth: 100,
                                        maxWidth: 100,
                                        isResizable: false,
                                        className: styles.moneyCell,
                                        onRender: (item: Kunde) => formatMoney(item?.umsatzVj1),
                                    },
                                    {
                                        key: 'umsatzVj2',
                                        name: `Umsatz ${thisYear - 2}`,
                                        fieldName: 'umsatzVj2',
                                        minWidth: 100,
                                        maxWidth: 100,
                                        isResizable: false,
                                        className: styles.moneyCell,
                                        onRender: (item: Kunde) => formatMoney(item?.umsatzVj2),
                                    },
                                    {
                                        key: 'umsatzVj3',
                                        name: `Umsatz ${thisYear - 3}`,
                                        fieldName: 'umsatzVj3',
                                        minWidth: 100,
                                        maxWidth: 100,
                                        isResizable: false,
                                        className: styles.moneyCell,
                                        onRender: (item: Kunde) => formatMoney(item?.umsatzVj3),
                                    },
                                ]}
                            />
                        </ScrollablePane>
                    </div>
                </>
            )}
            {assignBetreuerField && (
                <ConfirmMessage
                    title={assignBetreuerLabel + ' zuweisen'}
                    message={`Soll der ${assignBetreuerLabel} ${selectedKundeList.length} Kunden zugewiesen werden?`}
                    minWidth={1000}
                    content={
                        <div className={styles.confirmContainer}>
                            {renderBetreuer(assignBetreuerField, assignBetreuerLabel || '')}
                            {assigning !== null && (
                                <div>
                                    Aktualisiere {assigning}/{selectedKundeList.length}
                                </div>
                            )}
                        </div>
                    }
                    confirmDisabled={assigning !== null || !assignBetreuerNr}
                    onConfirm={async () => {
                        let n = 0;
                        // eslint-disable-next-line no-restricted-syntax
                        for (const kunde of selectedKundeList) {
                            n += 1;
                            setAssigning(n);

                            if (assignBetreuerField === 'betreuer') {
                                // eslint-disable-next-line no-await-in-loop
                                await assignKundeBetreuer({
                                    ...kunde,
                                    betreuer: assignBetreuerNr || 0,
                                });
                            } else if (assignBetreuerField === 'telBetreuer') {
                                // eslint-disable-next-line no-await-in-loop
                                await assignKundeTelBetreuer({
                                    ...kunde,
                                    telBetreuer: assignBetreuerNr || 0,
                                });
                            }
                        }

                        kundeList.refetch().then();

                        setAssignBetreuerField(null);
                        setAssignBetreuerNr(null);
                        setAssigning(null);
                    }}
                    onCancel={() => {
                        setAssignBetreuerField(null);
                        setAssignBetreuerNr(null);
                    }}
                />
            )}
        </Fabric>
    );
};
